import {
  CANVAS_OBJECT_TYPE_PREFIX,
  CANVAS_OBJECT_TYPE_SUFFIX,
} from '~/core/constants';

/**
 * The `canvasObjectMatcher` provides checker functions for each
 * `CANVAS_OBJECT_TYPE_SUFFIX` and `CANVAS_OBJECT_TYPE_PREFIX` values
 */

type CanvasObjectType = CANVAS_OBJECT_TYPE_PREFIX | CANVAS_OBJECT_TYPE_SUFFIX;

/**
 * Accepts a canvasObjTypePrefix value and returns a function that accepts the actual fabric object
 * @param canvasObjType the canvas prefix or suffix
 * @returns the curryfied function to check a fabric object
 */
const isFabricObject =
  (canvasObjType: CanvasObjectType) => (obj: fabric.Object) =>
    obj.name?.includes(canvasObjType) ?? false;

/**
 * Check if the object is highlighted
 * @param obj the fabricObject
 * @returns `true` if the object is highlighted, `false` otherwise
 */
export const isActiveShapeHighlights = isFabricObject(
  CANVAS_OBJECT_TYPE_PREFIX.ACTIVE_SHAPE_HIGHLIGHTS
);

/**
 * Check if the object is an appliance
 * @param obj the fabricObject
 * @returns `true` if the object is an appliance, `false` otherwise
 */
export const isAppliance = isFabricObject(CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE);

/**
 * Check if the object is a backsplash arrow
 * @param obj the fabricObject
 * @returns `true` if the object is a backsplash arrow, `false` otherwise
 */
export const isBacksplashArrow = isFabricObject(
  CANVAS_OBJECT_TYPE_PREFIX.BACKSPLASH_ARROW
);

/**
 * Check if the object is a backsplash group
 * @param obj the fabricObject
 * @returns `true` if the object is a backsplash group, `false` otherwise
 */
export const isBacksplashGroup = isFabricObject(
  CANVAS_OBJECT_TYPE_PREFIX.BACKSPLASH_GROUP
);

/**
 * Check if the object is a backsplash label
 * @param obj the fabricObject
 * @returns `true` if the object is a backsplash label, `false` otherwise
 */
export const isBacksplashLabel = isFabricObject(
  CANVAS_OBJECT_TYPE_PREFIX.BACKSPLASH_LABEL
);

/**
 * Check if the object is a backsplash measure label
 * @param obj the fabricObject
 * @returns `true` if the object is a backsplash measure label, `false` otherwise
 */
export const isBacksplashMeasureLabel = isFabricObject(
  CANVAS_OBJECT_TYPE_PREFIX.BACKSPLASH_MEASURE_LABEL
);

/**
 * Check if the object is a bump out
 * @param obj the fabricObject
 * @returns `true` if the object is a bump out, `false` otherwise
 */
export const isBumpOut = isFabricObject(CANVAS_OBJECT_TYPE_PREFIX.BUMP_OUT);

/**
 * Check if the object is a bump out arrow
 * @param obj the fabricObject
 * @returns `true` if the object is a bump out arrow, `false` otherwise
 */
export const isBumpOutArrow = isFabricObject(
  CANVAS_OBJECT_TYPE_PREFIX.BUMPOUT_ARROW
);

/**
 * Check if the object is a corner radius arc
 * @param obj the fabricObject
 * @returns `true` if the object is a corner radius arc, `false` otherwise
 */
export const isCornerRadiusArc = (obj: fabric.Object): boolean =>
  obj.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.CORNER_RADIUS_ARC) ?? false;

/**
 * Check if the object is a corner radius circle for diagonal
 * @param obj the fabricObject
 * @returns `true` if the object is a corner radius circle for diagonal, `false` otherwise
 */
export const isCornerRadiusCircleForDiagonal = isFabricObject(
  CANVAS_OBJECT_TYPE_PREFIX.CORNER_RADIUS_CIRCLE_FOR_DIAGONAL
);

/**
 * Check if the object is a corner radius circle for rounding
 * @param obj the fabricObject
 * @returns `true` if the object is a corner radius circle for rounding, `false` otherwise
 */
export const isCornerRadiusCircleForRounding = isFabricObject(
  CANVAS_OBJECT_TYPE_PREFIX.CORNER_RADIUS_CIRCLE_FOR_ROUNDING
);

/**
 * Check if the object is a corner radius circle
 * @param obj the fabricObject
 * @returns `true` if the object is a corner radius circle, `false` otherwise
 */
export const isCornerRadiusCircle = isFabricObject(
  CANVAS_OBJECT_TYPE_PREFIX.CORNER_RADIUS_CIRCLE
);

/**
 * Check if the object is a corner radius measurement
 * @param obj the fabricObject
 * @returns `true` if the object is a corner radius measurement, `false` otherwise
 */
export const isCornerRadiusMeasurement = isFabricObject(
  CANVAS_OBJECT_TYPE_PREFIX.CORNER_RADIUS_MEASUREMENT
);

/**
 * Check if the object is a diagonal corner
 * @param obj the fabricObject
 * @returns `true` if the object is a diagonal corner, `false` otherwise
 */
export const isDiagonalCorner = isFabricObject(
  CANVAS_OBJECT_TYPE_PREFIX.DIAGONAL
);

/**
 * Check if the object has an edge name
 * @param obj the fabricObject
 * @returns `true` if the object has an edge name, `false` otherwise
 */
export const hasEdgeName = isFabricObject(CANVAS_OBJECT_TYPE_PREFIX.EDGE_NAME);

/**
 * Check if the object has a free drawing temporal length
 * @param obj the fabricObject
 * @returns `true` if the object has a free drawing temporal length, `false` otherwise
 */
export const hasFreeDrawingTempLength = isFabricObject(
  CANVAS_OBJECT_TYPE_PREFIX.FREE_DRAWING_TEMP_LENGTH
);

/**
 * Check if the object has a free drawing temporal shape
 * @param obj the fabricObject
 * @returns `true` if the object has a free drawing temporal shape, `false` otherwise
 */
export const hasFreeDrawingTempShape = isFabricObject(
  CANVAS_OBJECT_TYPE_PREFIX.FREE_DRAWING_TEMP_SHAPE
);

/**
 * Check if the object is an interior arrow
 * @param obj the fabricObject
 * @returns `true` if the object is an interior arrow, `false` otherwise
 */
export const isInteriorArrow = isFabricObject(
  CANVAS_OBJECT_TYPE_PREFIX.INTERIOR_ARROW
);

/**
 * Check if the object is a polygon backsplash
 * @param obj the fabricObject
 * @returns `true` if the object is a polygon backsplash, `false` otherwise
 */
export const isPolygonBacksplash = isFabricObject(
  CANVAS_OBJECT_TYPE_PREFIX.POLYGON_BACKSPLASH
);

/**
 * Check if the object is a radius out
 * @param obj the fabricObject
 * @returns `true` if the object is a radius out, `false` otherwise
 */
export const isRadiusOut = isFabricObject(CANVAS_OBJECT_TYPE_PREFIX.RADIUS_OUT);

/**
 * Check if the object is a selection circle
 * @param obj the fabricObject
 * @returns `true` if the object is a selection circle, `false` otherwise
 */
export const isSelectionCircle = isFabricObject(
  CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE
);

/**
 * Check if the object is a shape edge
 * @param obj the fabricObject
 * @returns `true` if the object is a shape edge, `false` otherwise
 */
export const isShapeEdge = isFabricObject(CANVAS_OBJECT_TYPE_PREFIX.SHAPE_EDGE);

/**
 * Check if the object is a shape label
 * @param obj the fabricObject
 * @returns `true` if the object is a shape label, `false` otherwise
 */
export const isShapeLabel = isFabricObject(
  CANVAS_OBJECT_TYPE_PREFIX.SHAPE_LABEL
);

/**
 * Check if the object is the main shape
 * @param obj the fabricObject
 * @returns `true` if the object is the main shape, `false` otherwise
 */
export const isMainShape = isFabricObject(CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MAIN);

/**
 * Check if the object has a shape measure label
 * @param obj the fabricObject
 * @returns `true` if the object has a shape measure label, `false` otherwise
 */
export const hasShapeMeasureLabel = isFabricObject(
  CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MEASURE_LABEL
);

/**
 * Check if the object is a group containing shapes
 * @param obj the fabricObject
 * @returns `true` if the object is a group containing shapes, `false` otherwise
 */
export const isShapeGroup = isFabricObject(
  CANVAS_OBJECT_TYPE_SUFFIX.SHAPE_GROUP
);
